import React, { useState, useEffect } from 'react';
import {
  Building2,
  Zap,
  PercentCircle,
  FileCheck,
  ArrowRight,
  Calculator,
  CheckCircle2,
  TrendingUp,
  Shield,
  Clock,
  BadgeCheck,
  Sparkles,
  Users,
  Star,
  Coins,
} from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const FinancialServices = ({ onCompareLoan }) => {
  const { formatPrice, symbol, selectedCountry, t } = useCurrency();
  const [loanAmount, setLoanAmount] = useState(500000);
  const [tenure, setTenure] = useState(36);
  const [interestRate, setInterestRate] = useState(
    selectedCountry.code === 'IN' ? 10.45 : 3.5,
  );

  useEffect(() => {
    setInterestRate(selectedCountry.code === 'IN' ? 10.45 : 3.5);
  }, [selectedCountry.code]);

  // EMI calculation
  const monthlyRate = Number(interestRate) / 12 / 100;
  const emi =
    monthlyRate === 0
      ? Math.round(loanAmount / tenure)
      : Math.round(
          (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
            (Math.pow(1 + monthlyRate, tenure) - 1),
        );

  const isIndia = selectedCountry.code === 'IN';

  const trustIndicators = [
    {
      icon: Building2,
      value: t('finance.stats.bankPartners.value'),
      label: t('finance.stats.bankPartners.label'),
      sublabel: t('finance.stats.bankPartners.sublabel'),
    },
    {
      icon: Clock,
      value: t('finance.stats.quickApproval.value'),
      label: t('finance.stats.quickApproval.label'),
      sublabel: t('finance.stats.quickApproval.sublabel'),
    },
    {
      icon: PercentCircle,
      value: t('finance.stats.startingRate.value'),
      label: t('finance.stats.startingRate.label'),
      sublabel: t('finance.stats.startingRate.sublabel'),
    },
    {
      icon: FileCheck,
      value: t('finance.stats.digitalProcess.value'),
      label: t('finance.stats.digitalProcess.label'),
      sublabel: t('finance.stats.digitalProcess.sublabel'),
    },
  ];

  const benefitsList = t('finance.benefits');
  const benefits = [
    { icon: Shield, text: benefitsList[0] },
    { icon: BadgeCheck, text: benefitsList[1] },
    { icon: Sparkles, text: benefitsList[2] },
    { icon: Users, text: `25,000+ ${benefitsList[3]}` },
  ];

  const partnerBanks = isIndia
    ? [
        'HDFC Bank',
        'ICICI Bank',
        'Axis Bank',
        'Kotak',
        'Yes Bank',
        'IDFC First',
      ]
    : ['Maybank', 'CIMB', 'Public Bank', 'RHB', 'Hong Leong', 'AmBank'];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Trustworthy Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg shadow-amber-500/25">
              <Coins className="h-4 w-4" />
              {isIndia
                ? `₹${t('finance.badgeAmount')} ${t('finance.badge')}`
                : `RM ${t('finance.badgeAmountMY')} ${t('finance.badge')}`}
            </div>

            {/* Heading */}
            <div>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {t('finance.headline')}{' '}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {t('finance.headlineHighlight')}
                </span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {isIndia
                  ? t('finance.description')
                  : t('finance.descriptionMY')}
              </p>
            </div>

            {/* Trust Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {trustIndicators.map((item, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-5 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 bg-blue-50 group-hover:bg-blue-100 rounded-xl flex items-center justify-center transition-colors">
                      <item.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {item.value}
                      </div>
                      <div className="text-sm font-semibold text-slate-700">
                        {item.label}
                      </div>
                      <div className="text-xs text-slate-500">
                        {item.sublabel}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Benefits List */}
            <div className="bg-slate-900 rounded-2xl p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-400" />
                {t('finance.whyFinance')}
              </h3>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="h-8 w-8 bg-white/10 rounded-lg flex items-center justify-center">
                      <benefit.icon className="h-4 w-4 text-emerald-400" />
                    </div>
                    <span className="text-slate-300 text-sm">
                      {benefit.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Partner Banks */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-semibold">
                {t('finance.trustedPartners')}
              </p>
              <div className="flex flex-wrap gap-2">
                {partnerBanks.map((bank) => (
                  <span
                    key={bank}
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-full text-xs font-medium hover:border-blue-300 hover:text-blue-600 transition-colors"
                  >
                    {bank}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Interactive EMI Calculator Card */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-700/50">
              {/* Calculator Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-white font-bold text-xl">
                    {t('finance.emiCalculator')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('finance.estimatePayment')}
                  </p>
                </div>
                <div className="h-14 w-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
              </div>

              {/* Calculator Inputs */}
              <div className="space-y-5 mb-6">
                {/* Loan Amount Slider */}
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-slate-400 text-sm font-medium">
                      {t('finance.loanAmount')}
                    </span>
                    <span className="text-white font-bold text-xl">
                      {isIndia ? formatPrice(loanAmount) : 'RM' + loanAmount}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={isIndia ? 100000 : 100000}
                    max={isIndia ? 2000000 : 300000}
                    step="50000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer accent-amber-500"
                    data-testid="loan-amount-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>{isIndia ? 100000 : 100000}</span>
                    <span>{isIndia ? 2000000 : 300000}</span>
                  </div>
                </div>

                {/* Tenure Selector */}
                <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-slate-400 text-sm font-medium">
                      {t('finance.loanTenure')}
                    </span>
                    <span className="text-white font-bold text-xl">
                      {tenure} {t('finance.months')}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[12, 24, 36, 48].map((ten) => (
                      <button
                        key={ten}
                        onClick={() => setTenure(ten)}
                        data-testid={`tenure-${ten}-btn`}
                        className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                          tenure === ten
                            ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {ten}M
                      </button>
                    ))}
                  </div>
                </div>

                {/* Interest Rate Display */}
                <div className="flex items-center justify-between bg-white/5 rounded-xl p-3 border border-white/10">
                  <span className="text-slate-400 text-sm">
                    {t('finance.interestRate')}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 bg-emerald-500/10 px-2 py-1 rounded-lg border border-emerald-500/20 hover:border-emerald-500/40 transition-all">
                      <input
                        type="number"
                        value={interestRate}
                        onChange={(e) =>
                          setInterestRate(Number(e.target.value))
                        }
                        className="bg-transparent text-emerald-400 font-bold w-12 text-right focus:outline-none focus:ring-0 border-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        step="0.1"
                        min="0"
                      />
                      <span className="text-emerald-400 font-bold text-sm">
                        % p.a.
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 bg-emerald-500/20 px-2 py-0.5 rounded-full">
                      {t('finance.bestRate')}
                    </span>
                  </div>
                </div>
              </div>

              {/* EMI Result */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-5 text-center mb-4 shadow-lg shadow-amber-500/30">
                <p className="text-amber-100 text-sm font-medium mb-1">
                  {t('finance.monthlyEmi')}
                </p>
                <p
                  className="text-4xl font-black text-white"
                  data-testid="emi-result"
                >
                  {/* {formatPrice(emi)} */}
                  {selectedCountry?.symbol} {emi}{' '}
                </p>
                <p className="text-amber-100/80 text-xs mt-1">
                  {t('finance.totalPayable')}: {selectedCountry?.symbol}{' '}
                  {emi * tenure}
                </p>
              </div>

              {/* CTA Button */}
              <button
                onClick={onCompareLoan}
                data-testid="finance-compare-btn"
                className="w-full bg-white hover:bg-slate-100 text-slate-900 py-4 rounded-2xl font-bold transition-all hover:-translate-y-0.5 hover:shadow-xl flex items-center justify-center gap-2 group"
              >
                <Calculator className="h-5 w-5" />
                {t('finance.compareOffers')}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Trust Badge */}
              <div className="flex items-center justify-center gap-2 mt-4 text-slate-400 text-xs">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                <span>{t('finance.secureComparison')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinancialServices;
